import React, { useState, useEffect } from 'react';
import './ResultadosBusqueda.css';
import FormularioReserva from './FormularioReserva';
import Modal from './Modal';
import Carousel from './Carousel';
import BotonesMovilesPortal from './BotonesMovilesPortal';
import { formatearFecha, formatearPrecio } from '../utils/dateUtils';
import { obtenerPrecioPorOcupacion, obtenerPrecioPorOcupacionYFecha } from '../services/preciosService';

const ResultadosBusqueda = ({ 
  habitacionesDisponibles, 
  numeroPersonas, 
  fechaEntrada, 
  fechaSalida, 
  onReservaExitosa,
  onVolverABuscar,
  habitacionPreseleccionada
}) => {
  const [habitacionesSeleccionadas, setHabitacionesSeleccionadas] = useState([]);
  const [distribucionPersonas, setDistribucionPersonas] = useState({});
  const [personasAsignadas, setPersonasAsignadas] = useState(0);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [mostrarResumen, setMostrarResumen] = useState(false);
  const [reservaExitosa, setReservaExitosa] = useState(false);
  const [codigoReserva, setCodigoReserva] = useState(null);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    message: ''
  });
  const [modalConfirmacion, setModalConfirmacion] = useState({
    isOpen: false,
    message: "Antes de confirmar su reserva, tenga en cuenta que:\n\n• No incluimos servicio de desayuno\n• Disponemos de cocina compartida con café y té\n• No contamos con estacionamiento privado (pero es seguro dejar el auto afuera)\n\nSi está de acuerdo con estas condiciones, puede continuar con su reserva."
  });

  // Estado para almacenar los precios por temporada de cada habitación
  const [preciosPorTemporada, setPreciosPorTemporada] = useState({});

  // Efecto para manejar la selección automática de la habitación preseleccionada
  useEffect(() => {
    console.log("Effect for habitacionPreseleccionada triggered");
    console.log("habitacionPreseleccionada:", habitacionPreseleccionada);
    console.log("habitacionesDisponibles length:", habitacionesDisponibles.length);
    console.log("numeroPersonas:", numeroPersonas);
    
    if (habitacionPreseleccionada && habitacionesDisponibles.length > 0) {
      const habitacionEncontrada = habitacionesDisponibles.find(h => h.id === habitacionPreseleccionada);
      console.log("habitacionEncontrada:", habitacionEncontrada);
      
      if (habitacionEncontrada) {
        // Seleccionar automáticamente la habitación
        console.log("Setting habitacionesSeleccionadas:", [habitacionEncontrada]);
        setHabitacionesSeleccionadas([habitacionEncontrada]);
        
        // Establecer la distribución de personas según el número total de personas
        const personasHabitacion = Math.min(parseInt(numeroPersonas), habitacionEncontrada.capacity || habitacionEncontrada.capacidad || 1);
        console.log("Setting distribucionPersonas with personasHabitacion:", personasHabitacion);
        setDistribucionPersonas({
          [habitacionEncontrada.id]: personasHabitacion
        });
        
        // Actualizar el total de personas asignadas
        console.log("Setting personasAsignadas to:", personasHabitacion);
        setPersonasAsignadas(personasHabitacion);
        
        // Si el número de personas coincide con la capacidad, mostrar el resumen
        if (personasHabitacion === parseInt(numeroPersonas)) {
          console.log("Showing summary after timeout");
          setTimeout(() => {
            setMostrarResumen(true);
          }, 500);
        }
      }
    }
  }, [habitacionPreseleccionada, habitacionesDisponibles, numeroPersonas]);

  // Efecto para limpiar las habitaciones seleccionadas cuando cambien los parámetros de búsqueda
  useEffect(() => {
    console.log("Effect for cleaning selections triggered");
    console.log("habitacionPreseleccionada:", habitacionPreseleccionada);
    
    // Solo limpiamos si no hay habitación preseleccionada
    if (!habitacionPreseleccionada) {
      console.log("Cleaning selections because no habitacionPreseleccionada");
    setHabitacionesSeleccionadas([]);
    setMostrarResumen(false);
    setDistribucionPersonas({});
      setPersonasAsignadas(0); // También debemos resetear personasAsignadas
    }
  }, [habitacionesDisponibles, numeroPersonas, fechaEntrada, fechaSalida, habitacionPreseleccionada]);

  // Efecto para cargar los precios por temporada cuando cambien las fechas
  useEffect(() => {
    const cargarPreciosPorTemporada = async () => {
      console.log('ResultadosBusqueda - Cargando precios por temporada para fecha:', fechaEntrada);
      const precios = {};
      
      for (const habitacion of habitacionesDisponibles) {
        precios[habitacion.id] = {};
        
        for (let ocupacion = 1; ocupacion <= habitacion.capacity || habitacion.capacidad || 1; ocupacion++) {
          console.log(`Consultando precio para habitación ${habitacion.id}, ocupación ${ocupacion}`);
          // Obtener precio por temporada para la fecha de entrada
          const precio = await obtenerPrecioPorOcupacionYFecha(
            habitacion,
            ocupacion,
            fechaEntrada
          );
          
          precios[habitacion.id][ocupacion] = precio;
          console.log(`Precio obtenido: ${precio}`);
        }
      }
      
      console.log('Precios por temporada cargados:', precios);
      setPreciosPorTemporada(precios);
    };
    
    cargarPreciosPorTemporada();
  }, [habitacionesDisponibles, fechaEntrada]);

  // Función para obtener el precio según la ocupación, considerando temporadas
  const obtenerPrecioHabitacion = (habitacion, ocupacion) => {
    // Convertir ocupación a número para asegurarnos de que las comparaciones sean correctas
    const ocupacionNum = parseInt(ocupacion) || 1;
    
    // Si la ocupación es mayor a la capacidad, usar la capacidad máxima
    const ocupacionAjustada = Math.min(ocupacionNum, habitacion.capacity || habitacion.capacidad || 1);
    
    // Registrar información de diagnóstico
    console.log(`Calculando precio para habitación ${habitacion.id}, ocupación ${ocupacionNum}, ajustada a ${ocupacionAjustada}`);
    console.log(`Datos de habitación:`, habitacion);
    console.log(`Precios por temporada:`, preciosPorTemporada[habitacion.id]);
    console.log(`Precios por ocupación:`, habitacion.preciosPorOcupacion);
    
    // Primero, intentar obtener el precio de temporada
    if (
      preciosPorTemporada[habitacion.id] && 
      preciosPorTemporada[habitacion.id][ocupacionAjustada]
    ) {
      const precioTemporada = parseInt(preciosPorTemporada[habitacion.id][ocupacionAjustada]);
      console.log(`Usando precio de temporada para habitación ${habitacion.id}: ${precioTemporada}`);
      return precioTemporada;
    }
    
    // Si no hay precio por temporada, usar la función normal
    const precio = obtenerPrecioPorOcupacion(habitacion, ocupacionAjustada);
    console.log(`Precio final para habitación ${habitacion.id}: ${precio}`);
    return precio;
  };

  // Función para calcular la duración de la estancia en días
  const calcularDuracionEstancia = () => {
    try {
      // Asegurarnos de usar el formato correcto yyyy-mm-dd para los cálculos internos
      // Las fechas vienen en formato yyyy-mm-dd de los inputs date
      if (!fechaEntrada || !fechaSalida) return 0;
      
      // Dividir las fechas para evitar problemas de zona horaria
      const [anioE, mesE, diaE] = fechaEntrada.split('-');
      const [anioS, mesS, diaS] = fechaSalida.split('-');
      
      if (!anioE || !mesE || !diaE || !anioS || !mesS || !diaS) {
        console.error("Formato de fecha incorrecto");
        return 0;
      }
      
      // Crear fechas con tiempo fijo (12:00 PM) para evitar problemas de zona horaria
      // El mes en JavaScript es base 0 (enero = 0)
      const fechaInicioObj = new Date(parseInt(anioE), parseInt(mesE) - 1, parseInt(diaE), 12, 0, 0);
      const fechaFinObj = new Date(parseInt(anioS), parseInt(mesS) - 1, parseInt(diaS), 12, 0, 0);
      
      // Calcular la diferencia en milisegundos y convertirla a días
      const diffTiempo = fechaFinObj.getTime() - fechaInicioObj.getTime();
      // La diferencia en días es exactamente diffTiempo / (1000 * 60 * 60 * 24)
      // No necesitamos Math.ceil ya que queremos el número exacto de días (noches)
      const diffDias = diffTiempo / (1000 * 60 * 60 * 24);
      
      return diffDias > 0 ? diffDias : 0;
    } catch (error) {
      console.error("Error al calcular duración:", error);
      return 0; // Valor por defecto en caso de error
    }
  };
  
  // Función para calcular el precio total
  const calcularPrecioTotal = () => {
    // Usar el valor redondeado de la duración de la estancia
    const duracionEstancia = Math.round(calcularDuracionEstancia());
    
    console.log("Calculating total price");
    console.log("habitacionesSeleccionadas:", habitacionesSeleccionadas);
    console.log("distribucionPersonas:", distribucionPersonas);
    console.log("duracionEstancia:", duracionEstancia);
    
    // Calcular el precio total de las habitaciones según su ocupación
    const sumaPreciosHabitaciones = habitacionesSeleccionadas.reduce(
      (total, habitacion) => {
        const precioHabitacion = obtenerPrecioHabitacion(habitacion, distribucionPersonas[habitacion.id] || 1);
        console.log(`Precio para habitación ${habitacion.id}, personas: ${distribucionPersonas[habitacion.id] || 1}, precio: ${precioHabitacion}`);
        return total + precioHabitacion;
      }, 0
    );
    
    console.log("sumaPreciosHabitaciones:", sumaPreciosHabitaciones);
    console.log("Total price:", sumaPreciosHabitaciones * duracionEstancia);
    
    // El resultado ya usa la duración redondeada, así que es más preciso
    return sumaPreciosHabitaciones * duracionEstancia;
  };

  const handleSeleccionarHabitacion = (habitacion) => {
    const estaSeleccionada = habitacionesSeleccionadas.some(h => h.id === habitacion.id);
    
    if (estaSeleccionada) {
      // Si la habitación ya está seleccionada, la removemos
      const nuevasHabitaciones = habitacionesSeleccionadas.filter(h => h.id !== habitacion.id);
      setHabitacionesSeleccionadas(nuevasHabitaciones);
      
      // Removemos la distribución de personas para esta habitación
      const nuevaDistribucion = { ...distribucionPersonas };
      delete nuevaDistribucion[habitacion.id];
      setDistribucionPersonas(nuevaDistribucion);
      
      // Actualizamos el total de personas asignadas
      const personasEnHabitacion = distribucionPersonas[habitacion.id] || 0;
      setPersonasAsignadas(prev => prev - personasEnHabitacion);
    } else {
      // Si la habitación no está seleccionada, la agregamos
      setHabitacionesSeleccionadas([...habitacionesSeleccionadas, habitacion]);
      
      // Inicializamos con 1 persona si no hay distribución previa
      if (!distribucionPersonas[habitacion.id]) {
        setDistribucionPersonas({
          ...distribucionPersonas,
          [habitacion.id]: 1
        });
        setPersonasAsignadas(prev => prev + 1);
      }
    }
  };

  const handleCambiarOcupacion = (habitacionId, nuevaOcupacion) => {
    const habitacion = habitacionesSeleccionadas.find(h => h.id === habitacionId);
    if (!habitacion) return;

    const ocupacionActual = distribucionPersonas[habitacionId] || 0;
    const diferencia = nuevaOcupacion - ocupacionActual;

    setDistribucionPersonas({
      ...distribucionPersonas,
      [habitacionId]: nuevaOcupacion
    });

    setPersonasAsignadas(prev => prev + diferencia);
  };

  const handleContinuar = () => {
    if (personasAsignadas !== parseInt(numeroPersonas)) {
      setModalConfig({
        isOpen: true,
        message: `Debe asignar exactamente ${numeroPersonas} personas entre las habitaciones seleccionadas. Actualmente hay ${personasAsignadas} personas asignadas.`
      });
      return;
    }

    // Verificar que ninguna habitación exceda su capacidad
    const habitacionExcedida = habitacionesSeleccionadas.find(habitacion => {
      const personasEnHabitacion = distribucionPersonas[habitacion.id] || 0;
      return personasEnHabitacion > (habitacion.capacity || habitacion.capacidad || 1);
    });

    if (habitacionExcedida) {
      setModalConfig({
        isOpen: true,
        message: `La habitación ${habitacionExcedida.nombre} excede su capacidad máxima de ${habitacionExcedida.capacity || habitacionExcedida.capacidad || 1} personas.`
      });
      return;
    }

    setMostrarResumen(true);
  };

  const handleReservar = () => {
    setModalConfirmacion({ ...modalConfirmacion, isOpen: true });
  };

  const handleConfirmarReserva = () => {
    console.log("handleConfirmarReserva called");
    console.log("Current state before confirming:");
    console.log("- habitacionesSeleccionadas:", habitacionesSeleccionadas);
    console.log("- distribucionPersonas:", distribucionPersonas);
    console.log("- personasAsignadas:", personasAsignadas);
    console.log("- numeroPersonas:", numeroPersonas);
    
    setModalConfirmacion({ ...modalConfirmacion, isOpen: false });
    setMostrarFormulario(true);
    setMostrarResumen(false);
  };

  const handleCancelarReserva = () => {
    setModalConfirmacion({ ...modalConfirmacion, isOpen: false });
  };

  const handleReservaExitosa = (codigo) => {
    setReservaExitosa(true);
    setCodigoReserva(codigo);
    setMostrarFormulario(false);
    setHabitacionesSeleccionadas([]);
    onReservaExitosa();
  };

  const handleVolverABuscar = () => {
    console.log("handleVolverABuscar called in ResultadosBusqueda");
    
    // Reset all component state
    setReservaExitosa(false);
    setHabitacionesSeleccionadas([]);
    setMostrarResumen(false);
    setDistribucionPersonas({});
    setPersonasAsignadas(0);
    
    // Important: If we call onVolverABuscar, we need to reset everything in the parent component too
    // This ensures the full search form is shown again and the preselected room is cleared
    if (onVolverABuscar) {
      onVolverABuscar();
    }
  };

  const cerrarModal = () => {
    setModalConfig({
      ...modalConfig,
      isOpen: false
    });
  };

  if (reservaExitosa) {
    return (
      <div className="mensaje-exito">
        <h3>¡Reserva realizada con éxito!</h3>
        <p>Tu código de reserva es: <strong>{codigoReserva}</strong></p>
        <p>Gracias por elegir nuestro hotel. Te hemos enviado un correo con los detalles de tu reserva.</p>
        <button onClick={handleVolverABuscar} className="btn-volver">
          <span className="icono-reiniciar">←</span> Volver
        </button>
      </div>
    );
  }

  if (mostrarFormulario) {
    console.log("Rendering FormularioReserva with:");
    console.log("- habitacionesSeleccionadas:", habitacionesSeleccionadas);
    console.log("- distribucionPersonas:", distribucionPersonas);
    console.log("- personasAsignadas:", personasAsignadas);
    
    return (
      <FormularioReserva
        habitaciones={habitacionesSeleccionadas}
        fechaEntrada={fechaEntrada}
        fechaSalida={fechaSalida}
        precioTotal={calcularPrecioTotal()}
        duracionEstancia={calcularDuracionEstancia()}
        distribucionPersonas={distribucionPersonas}
        onCancelar={() => {
          setMostrarFormulario(false);
          setMostrarResumen(true);
        }}
        onReservaExitosa={handleReservaExitosa}
      />
    );
  }

  return (
    <div className="resultados-busqueda">
      <Modal
        isOpen={modalConfig.isOpen}
        onClose={cerrarModal}
        message={modalConfig.message}
        secondaryAction={modalConfig.secondaryAction}
        secondaryButtonText={modalConfig.secondaryButtonText}
      />
      
      <Modal
        isOpen={modalConfirmacion.isOpen}
        onClose={handleCancelarReserva}
        message={modalConfirmacion.message}
        primaryAction={handleConfirmarReserva}
        primaryButtonText="Continuar"
        secondaryAction={handleCancelarReserva}
        secondaryButtonText="Cancelar"
      />
      
      {/* Header para escritorio */}
      <div className="resultados-header">
        <button onClick={handleVolverABuscar} className="btn-volver-a-buscar">
          <span className="icono-reiniciar">←</span> Volver
        </button>
        <div className="resumen-seleccion-actual">
          <div className="resumen-item">
            <span className="resumen-label">Personas:</span>
            <span className="resumen-valor">
              {personasAsignadas} de {numeroPersonas} asignadas
              {personasAsignadas < parseInt(numeroPersonas) && 
                <span className="resumen-faltante"> ({parseInt(numeroPersonas) - personasAsignadas} faltantes)</span>
              }
            </span>
          </div>
        </div>
        {!mostrarResumen && (
          <button 
            onClick={handleContinuar}
            className={`btn-continuar ${personasAsignadas === parseInt(numeroPersonas) ? '' : 'btn-deshabilitado'}`}
          >
            Continuar
          </button>
        )}
        {mostrarResumen && (
          <button 
            onClick={handleReservar} 
            className="btn-continuar"
          >
            Continuar
          </button>
        )}
      </div>
      
      {!mostrarResumen && (
        <>
          <h3 className="titulo-movil">Seleccionar Habitaciones</h3>
          <p>Distribuya las {numeroPersonas} personas entre las habitaciones disponibles</p>
          <div className="grid-habitaciones">
            {habitacionesDisponibles.map((habitacion) => (
              <div
                key={habitacion.id}
                className={`habitacion-card ${habitacionesSeleccionadas.some(h => h.id === habitacion.id) ? 'habitacion-seleccionada' : ''}`}
              >
                <Carousel 
                  imagenes={habitacion.imagenes || [habitacion.imagen]} 
                  altura="180px"
                />
                <div className="habitacion-info">
                  <h4>{habitacion.nombre}</h4>
                  <p>{habitacion.descripcion}</p>
                  {habitacionesSeleccionadas.some(h => h.id === habitacion.id) && (
                    <div className="selector-personas">
                      <label>Personas en esta habitación:</label>
                      <select
                        value={distribucionPersonas[habitacion.id] || 1}
                        onChange={(e) => handleCambiarOcupacion(habitacion.id, parseInt(e.target.value))}
                      >
                        {Array.from({ length: habitacion.capacity || habitacion.capacidad || 1 }, (_, i) => i + 1).map(num => (
                          <option key={num} value={num}>{num} {num === 1 ? 'persona' : 'personas'}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="precio-ocupacion">
                    <p>Precio según ocupación:</p>
                    <ul className="lista-precios">
                      {Array.from({ length: habitacion.capacity || habitacion.capacidad || 1 }, (_, i) => i + 1).map(ocupacion => (
                        <li key={ocupacion} className={ocupacion === (distribucionPersonas[habitacion.id] || 1) ? 'precio-resaltado' : ''}>
                          {ocupacion} {ocupacion === 1 ? 'persona' : 'personas'}: {formatearPrecio(
                            preciosPorTemporada[habitacion.id] && preciosPorTemporada[habitacion.id][ocupacion] 
                              ? preciosPorTemporada[habitacion.id][ocupacion] 
                              : habitacion.preciosPorOcupacion[ocupacion]
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="seleccion-container">
                    <button 
                      className={`btn-seleccionar ${habitacionesSeleccionadas.some(h => h.id === habitacion.id) ? 'seleccionado' : ''}`}
                      onClick={() => handleSeleccionarHabitacion(habitacion)}
                    >
                      {habitacionesSeleccionadas.some(h => h.id === habitacion.id) 
                        ? <><span className="icono-check">✓</span> Seleccionado</> 
                        : 'Seleccionar'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      
      <BotonesMovilesPortal mostrar={!mostrarFormulario && !reservaExitosa && !mostrarResumen}>
        <button onClick={handleVolverABuscar} className="btn-volver-a-buscar">
          <span className="icono-reiniciar">←</span> Volver
        </button>
        <button 
          onClick={handleContinuar}
          className={`btn-continuar ${personasAsignadas === parseInt(numeroPersonas) ? '' : 'btn-deshabilitado'}`}
        >
          Continuar
        </button>
      </BotonesMovilesPortal>

      {mostrarResumen && (
        <div className="resumen-seleccion">
          <h4>Resumen de Reserva</h4>
          
          <div className="resumen-seccion">
            <div className="resumen-seccion-titulo">
              <span className="resumen-seccion-icon">📅</span>
              <span>Fechas y Duración</span>
            </div>
            <ul>
              <li>
                <span className="item-label">Check-in:</span>
                <span className="item-value">{formatearFecha(fechaEntrada)}</span>
              </li>
              <li>
                <span className="item-label">Check-out:</span>
                <span className="item-value">{formatearFecha(fechaSalida)}</span>
              </li>
              <li>
                <span className="item-label">Duración:</span>
                <span className="item-value">
                  {Math.round(calcularDuracionEstancia())} {Math.round(calcularDuracionEstancia()) === 1 ? 'noche' : 'noches'}
                </span>
              </li>
            </ul>
          </div>
          
          <div className="resumen-seccion">
            <div className="resumen-seccion-titulo">
              <span className="resumen-seccion-icon">👥</span>
              <span>Huéspedes</span>
            </div>
            <ul>
              <li>
                <span className="item-label">Total de personas:</span>
                <span className="item-value">{personasAsignadas}</span>
              </li>
            </ul>
          </div>
          
          <div className="resumen-seccion">
            <div className="resumen-seccion-titulo">
              <span className="resumen-seccion-icon">🏠</span>
              <span>Habitaciones</span>
            </div>
            <ul>
              {habitacionesSeleccionadas.map(habitacion => {
                const personas = distribucionPersonas[habitacion.id] || 1;
                return (
                  <li key={habitacion.id}>
                    <span className="item-label">
                      {habitacion.nombre} ({personas} {personas === 1 ? 'persona' : 'personas'})
                    </span>
                    <span className="item-value">
                      {formatearPrecio(obtenerPrecioHabitacion(habitacion, personas))}/noche
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          
          <div className="precio-total">
            <h4>Precio Total de la Estancia</h4>
            <p className="detalle-precio">
              {formatearPrecio(calcularPrecioTotal())}
              <br />
              <small>
                ({formatearPrecio(Math.round(habitacionesSeleccionadas.reduce(
                  (total, h) => total + obtenerPrecioHabitacion(h, distribucionPersonas[h.id] || 1), 0
                )))}/noche × {Math.round(calcularDuracionEstancia())} {Math.round(calcularDuracionEstancia()) === 1 ? 'noche' : 'noches'})
              </small>
            </p>
          </div>
        </div>
      )}
      
      <BotonesMovilesPortal mostrar={mostrarResumen && !mostrarFormulario && !reservaExitosa}>
        <button onClick={handleVolverABuscar} className="btn-volver-a-buscar">
          <span className="icono-reiniciar">←</span> Volver
        </button>
        <button onClick={handleReservar} className="btn-continuar">
          Continuar
        </button>
      </BotonesMovilesPortal>
    </div>
  );
};

export default ResultadosBusqueda; 